// Helpers for "several boxes" answers (see BoxesAnswer.tsx).

import {
  boxEntry,
  boxMarkerLetter,
  splitBoxMarkers,
} from '../../platform/content/boxAnswer';

export type AnswerBox = { id: string; label: string };

// Same limit as the single number box (see PracticeTask.tsx).
export const BOX_MAX_LENGTH = 15;

// The item's own boxes, read defensively: 2 to 6 of them, each with a
// single lower-case letter for its id, no letter used twice.
export function readBoxes(content: unknown): AnswerBox[] | null {
  if (typeof content !== 'object' || content === null) return null;
  const list = (content as Record<string, unknown>).boxes;
  if (!Array.isArray(list) || list.length < 2 || list.length > 6) return null;
  const valid = list.filter(
    (entry): entry is AnswerBox =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as Record<string, unknown>).id === 'string' &&
      /^[a-z]$/.test((entry as Record<string, unknown>).id as string) &&
      typeof (entry as Record<string, unknown>).label === 'string',
  );
  if (valid.length !== list.length) return null;
  return new Set(valid.map((box) => box.id)).size === valid.length
    ? valid
    : null;
}

// The boxes sit inside the sentence when the prompt has a marker ("[a]")
// for every one of them. Otherwise they go under it, each with its label.
export function boxesInSentence(prompt: string, boxes: AnswerBox[]): boolean {
  const marked = new Set(
    splitBoxMarkers(prompt).flatMap((part) =>
      typeof part === 'string' ? [] : [part.boxId],
    ),
  );
  return boxes.every((box) => marked.has(box.id));
}

// What a screen reader calls a box: its label, or "Box a" when the label is
// only its marker.
export function boxName(box: AnswerBox): string {
  const letter = boxMarkerLetter(box.label);
  return letter === null ? box.label : `Box ${letter}`;
}

// The practice screen keeps every answer as one piece of text. For boxes
// that is one line per box, "id=what was typed" -- exactly as typed; it is
// only tidied into the agreed number form when it is sent.
export function splitBoxValues(value: string): Record<string, string> {
  const values: Record<string, string> = {};
  for (const line of value.split('\n')) {
    const cut = line.indexOf('=');
    if (cut > 0) values[line.slice(0, cut)] = line.slice(cut + 1);
  }
  return values;
}

// Empty text while every box is empty, so "nothing answered yet" reads the
// same as it does for every other kind of question.
export function joinBoxValues(
  boxes: AnswerBox[],
  values: Record<string, string>,
): string {
  const typed = boxes.map((box) => (values[box.id] ?? '').replace(/\n/g, ''));
  if (typed.every((text) => text.length === 0)) return '';
  return boxes
    .map((box, index) => `${box.id}=${typed[index] ?? ''}`)
    .join('\n');
}

// What is sent: one "<id>=<number>" for each box, in the boxes' own order.
// Null until every box holds a number.
export function boxesOrder(boxes: AnswerBox[], value: string): string[] | null {
  if (boxes.length === 0) return null;
  const values = splitBoxValues(value);
  const order: string[] = [];
  for (const box of boxes) {
    const entry = boxEntry(box.id, values[box.id] ?? '');
    if (entry === null) return null;
    order.push(entry);
  }
  return order;
}

// Half-typed is not wrong: "-", "3." and an empty box are simply not
// finished. Only text that can never become a number ("abc", "1/2") is
// flagged to assistive technology.
export function boxTextInvalid(text: string): boolean {
  return !/^[-−]?\d*\.?\d*$/.test(text.trim());
}

// One press on the number pad, for the box it types into. Digits go on the
// end; the point only if there is none yet; minus puts a minus sign at the
// front, or takes it off again.
export function pressBoxKey(text: string, key: string): string {
  if (key === '-') {
    if (/^[-−]/.test(text)) return text.slice(1);
    return text.length >= BOX_MAX_LENGTH ? text : `-${text}`;
  }
  if (key === '.' && text.includes('.')) return text;
  if (text.length >= BOX_MAX_LENGTH) return text;
  return text + key;
}
